import React, { useState } from 'react';

const CalendarDialog = ({ isOpen, onClose, onDateSelect, selectedDate, returnDate, tripType = 'roundTrip', fromCode = 'EWR', toCode = 'SDQ' }) => {

  // Parse selectedDate properly - it comes in as 'Thu, Sep 4' format
  const parseSelectedDate = (dateStr) => {
    if (!dateStr) return new Date(2025, 7, 28); // Default to Aug 28, 2025
    
    // Handle 'Thu, Sep 4' format by adding current year (2025)
    if (dateStr.includes(',')) {
      const parts = dateStr.split(',');
      if (parts.length === 2) {
        const dayMonth = parts[1].trim(); // 'Sep 4'
        const fullDateStr = `${dayMonth}, 2025`; // 'Sep 4, 2025'
        const parsed = new Date(fullDateStr);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    }
    
    // Fallback to direct parsing
    const directParse = new Date(dateStr);
    if (!isNaN(directParse.getTime())) {
      return directParse;
    }
    
    // Final fallback
    return new Date(2025, 7, 28);
  };

  // Get the actual current date
  const today = new Date();
  // Show the month that contains the selected date
  const initialMonth = parseSelectedDate(selectedDate);
  const [currentMonthState, setCurrentMonth] = useState(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
  const [activeFlightTab, setActiveFlightTab] = useState(0);
  const [showMonthBorder, setShowMonthBorder] = useState(false);
  
  const [departDate, setDepartDate] = useState(parseSelectedDate(selectedDate));
  const [returnDateState, setReturnDateState] = useState(returnDate ? parseSelectedDate(returnDate) : null);
  const [stopsFilter, setStopsFilter] = useState('3');
  const [classFilter, setClassFilter] = useState('');

  // Store initial dates to track changes
  const [initialDepartDate] = useState(parseSelectedDate(selectedDate));
  const [initialReturnDate] = useState(returnDate ? parseSelectedDate(returnDate) : null);

  // Check if dates have changed from initial values
  const hasDateChanges = () => {
    const departChanged = !isSameDay(departDate, initialDepartDate);
    const returnChanged = !isSameDay(returnDateState, initialReturnDate);
    return departChanged || returnChanged;
  };

  // Sample pricing data for each day of August 2025
  const getDayPrice = (date) => {
    const day = date.getDate();
    const priceData = {
      1: '12K', 2: '12K', 3: '12K', 4: '12K', 5: '12K', 6: '12K', 7: '12K',
      8: '13K', 9: '10K', 10: '10K', 11: '10K', 12: '16K', 13: '10K', 14: '17K',
      15: '10K', 16: '10K', 17: '10K', 18: '15K', 19: '20K', 20: '10K', 21: '15K',
      22: '10K', 23: '10K', 24: '10K', 25: '12K', 26: '11K', 27: '10K', 28: '13K',
      29: '15K', 30: '10K', 31: '14K'
    };
    return priceData[day] || '';
  };

  // Dynamic flight data based on selected dates
  const flightTabs = [
    {
      id: 0,
      date: departDate ? departDate.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }) : 'Select date',
      airports: `${fromCode} to ${toCode}`,
      price: departDate ? getDayPrice(departDate) : '',
      selected: true
    },
    {
      id: 1,
      date: returnDateState ? returnDateState.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }) : 'Select date',
      airports: `${toCode} to ${fromCode}`,
      price: returnDateState ? getDayPrice(returnDateState) : '',
      selected: false
    }
  ];

  const isLowestPrice = (date) => {
    return getDayPrice(date) === '10K';
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0); // Last day of current month
    const startWeekday = firstDay.getDay(); // Day of week for first day (0 = Sunday)
    const daysInMonth = lastDay.getDate(); // Number of days in month
    
    const days = [];
    
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < startWeekday; i++) {
      days.push(null);
    }
    
    // Add actual days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    // Fill remaining slots to complete the grid (up to 42 total)
    const totalSlots = Math.ceil(days.length / 7) * 7; // Round up to next multiple of 7
    while (days.length < totalSlots) {
      days.push(null);
    }
    
    return days;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };


  const isRangeStart = (date) => {
    if (!date || !departDate) return false;
    return isSameDay(date, departDate);
  };

  const isRangeEnd = (date) => {
    if (!date || !returnDateState) return false;
    return isSameDay(date, returnDateState);
  };

  const isInContinuousRange = (date) => {
    if (!date || !departDate || !returnDateState) return false;
    return date > departDate && date < returnDateState;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    
    const todayCompare = new Date();
    todayCompare.setHours(0, 0, 0, 0);
    
    // Normalize the date being checked to ensure proper comparison
    const dateCompare = new Date(date);
    dateCompare.setHours(0, 0, 0, 0);
    
    // For departure tab (tab 0), disable dates before today AND after return date
    if (activeFlightTab === 0) {
      if (dateCompare < todayCompare) return true;
      if (returnDateState) {
        const returnCompare = new Date(returnDateState);
        returnCompare.setHours(0, 0, 0, 0);
        return dateCompare > returnCompare;
      }
      return false;
    }
    
    // For return tab (tab 1), disable dates before today AND before departure date
    if (activeFlightTab === 1) {
      if (dateCompare < todayCompare) return true;
      if (departDate) {
        const departCompare = new Date(departDate);
        departCompare.setHours(0, 0, 0, 0);
        return dateCompare < departCompare;
      }
      return false;
    }
    
    return dateCompare < todayCompare;
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonthState.getFullYear(), currentMonthState.getMonth() - 1, 1));
    setShowMonthBorder(true);
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonthState.getFullYear(), currentMonthState.getMonth() + 1, 1));
    setShowMonthBorder(true);
  };

  // Check if previous month navigation should be disabled
  const isPreviousMonthDisabled = () => {
    const today = new Date();
    const currentMonth = currentMonthState.getMonth();
    const currentYear = currentMonthState.getFullYear();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();
    
    // Always disable if current month is today's month
    if (currentYear === todayYear && currentMonth === todayMonth) {
      return true;
    }
    
    // For return tab, disable if trying to go before departure date month
    if (activeFlightTab === 1 && departDate) {
      const departMonth = departDate.getMonth();
      const departYear = departDate.getFullYear();
      if (currentYear === departYear && currentMonth <= departMonth) {
        return true;
      }
    }
    
    return false;
  };

  // Check if next month navigation should be disabled
  const isNextMonthDisabled = () => {
    // For departure tab, disable if trying to go after return date month
    if (activeFlightTab === 0 && returnDateState) {
      const returnMonth = returnDateState.getMonth();
      const returnYear = returnDateState.getFullYear();
      const currentMonth = currentMonthState.getMonth();
      const currentYear = currentMonthState.getFullYear();
      
      if (currentYear === returnYear && currentMonth >= returnMonth) {
        return true;
      }
    }
    
    return false;
  };


  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    
    // Reset month border on date interaction
    setShowMonthBorder(false);
    
    // Always respect the active tab for date selection
    if (activeFlightTab === 0) {
      // Departure tab is active - set departure date
      setDepartDate(date);
    } else {
      // Return tab is active - set return date
      // For return date, ensure it's not before departure date
      if (departDate && date < departDate) {
        // If user clicks a date before departure while on return tab, do nothing
        return;
      }
      setReturnDateState(date);
    }
  };

  const handleApplyChanges = () => {
    setShowMonthBorder(false);
    onDateSelect({ departDate, returnDate: returnDateState });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white shadow-2xl shadow-gray-400 max-w-4xl w-full m-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-4 pb-0">
          <div className="flex-1">
            <h2 className="text-2xl  text-aa-blue mb-4">Calendar</h2>
          </div>
          <button
            onClick={() => {
              setShowMonthBorder(false);
              onClose();
            }}
            className="text-[#0078d2] hover:text-blue-700 border-2 border-[#0078d2] rounded-full hover:bg-blue-50 transition-colors w-10 h-10 flex items-center justify-center"
          >
            <span className="text-2xl">×</span>
          </button>
        </div>

        {/* Flight Tabs and Trip Summary in One Line */}
        <div className="px-4 shadow-lg">
          <div className="flex items-center justify-between py-4 border-b border-gray-200 ">
            {/* Flight Tabs on Left */}
            <div className="flex h-full -my-4">
              {flightTabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setShowMonthBorder(false);
                    setActiveFlightTab(index);
                  }}
                  className={`px-4 pb-4 pt-3 transition-colors min-w-[120px]  ${
                    activeFlightTab === index 
                      ? 'bg-aa-blue border-2 border-[#0078d2] p-[2px]' 
                      : 'border-transparent hover:bg-gray-50 bg-blue-200'
                  } ${index === 0 ? 'border-r border-gray-200 mr-0 rounded-tl-2xl' : 'rounded-tr-2xl'}`}
                >
                  <div className={`text-lg mb-1 font-semibold ${activeFlightTab === index ? 'text-white' : 'text-gray-900'}`}>{tab.date}</div>
                  <div className={`text-lg mb-1 font-semibold ${activeFlightTab === index ? 'text-white underline' : 'text-gray-900'}`}>{tab.airports}</div>
                  <div className={`text-sm font-semibold ${activeFlightTab === index ? 'text-white' : 'text-gray-900'}`}>
                    {tab.price}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Trip Summary on Right */}
            <div className="mr-10">
              <div className="text-lg text-gray-600 mb-1">Round trip (per person) from</div>
              <div className="text-[20px] font-normal text-[#003366]">22K miles</div>
              <div className="text-[20px] text-[#003366]">+ $135.09</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-10 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex gap-6">
              <div className="relative focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
                <select 
                  value={stopsFilter}
                  onChange={(e) => setStopsFilter(e.target.value)}
                  className="appearance-none bg-white border border-gray-600 rounded px-4 py-3 pr-10 text-base text-[#0078d2] min-w-[240px] focus:outline-none"
                >
                  <option value="0">Nonstop only</option>
                  <option value="1">1 stop or fewer</option>
                  <option value="2">2 stops or fewer</option>
                  <option value="3">Any number of stops</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-[#0078d2]" fill="currentColor" viewBox="0 0 12 12">
                    <path d="M6 8L2 4h8L6 8z" />
                  </svg>
                </div>
              </div>

              <div className="relative focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
                <select 
                  value={classFilter}
                  onChange={(e) => setClassFilter(e.target.value)}
                  className="appearance-none bg-white border border-gray-600 rounded px-4 py-3 pr-10 text-base text-[#0078d2] min-w-[240px] focus:outline-none"
                >
                  <option value="">Show all classes</option>
                  <option value="COACH">Main</option>
                  <option value="PREMIUM_COACH">Premium Economy</option>
                  <option value="BUSINESS,FIRST">Business / First</option>
                  <option value="FIRST">First</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-[#0078d2]" fill="currentColor" viewBox="0 0 12 12">
                    <path d="M6 8L2 4h8L6 8z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm bg-slate-200 p-4">
              <span 
                className="font-semibold text-lg text-white px-3 py-1"
                style={{
                  backgroundColor: '#008712',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontFamily: 'AmericanSansMedium, Calibri, Helvetica, Arial, sans-serif',
                  lineHeight: '1.15',
                  WebkitFontSmoothing: 'antialiased'
                }}
              >
                10K
              </span>
              <span className="text-gray-600">Lowest price this month</span>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="relative px-4 py-4">
          {/* Month Title (left aligned) */}
          <div className="flex justify-start mb-2 ml-6">
            <h3 className="text-xl font-semibold text-gray-600">
              {currentMonthState.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
          </div>

          {/* Calendar with Navigation Container */}
          <div className="relative flex items-center">
            {/* Left Navigation Arrow */}
            <button
              onClick={prevMonth}
              disabled={isPreviousMonthDisabled()}
              className={`flex items-center justify-center w-16 h-16 transition-colors z-10 mr-1 ${
                isPreviousMonthDisabled() 
                  ? 'cursor-not-allowed opacity-30' 
                  : 'hover:bg-blue-50 cursor-pointer'
              }`}
            >
              <svg className={`w-10 h-10 ${
                isPreviousMonthDisabled() ? 'text-gray-400' : 'text-aa-blue'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Calendar Container */}
            <div className={`p-4 flex-1 transition-all duration-300 rounded-lg ${
              stopsFilter === '0' ? 'border-2 border-transparent' : 
              showMonthBorder ? 'border-2 border-blue-200' : 'border-2 border-transparent'
            }`}>
            {/* Calendar Grid or No Flights Message */}
            {stopsFilter === '0' ? (
              // No flights available message for nonstop only
              <div className="flex flex-col items-center justify-center py-16">
                <div id="no-result-error" className="text-center">
                  {/* Warning Icon */}
                  <div className="mb-2">
                    <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <p className="no-result-filter-warning text-center text-xl font-normal text-gray-800 mb-2">No flights available</p>
                  <p className="no-result-filter-text text-center text-gray-600">Please try a different number of stops or class of service.</p>
                </div>
              </div>
            ) : (
              <>
              {/* Days of Week */}
              <div className="grid grid-cols-7 mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-lg font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Regular calendar grid */}
              <div className="grid grid-cols-7">
            {getDaysInMonth(currentMonthState).map((date, index) => {
              if (!date) {
                return <div key={index} className="h-16"></div>; // Empty cell
              }
              
              const price = getDayPrice(date);
              const isDisabled = isDateDisabled(date);
              // Show both selected dates, but highlight active tab's date more prominently
              const isDepartureSelected = departDate && isSameDay(date, departDate);
              const isReturnSelected = returnDateState && isSameDay(date, returnDateState);
              
              // Check if this date is the active tab's date (prominent) or inactive tab's date (lighter)
              const isActiveTabDate = (activeFlightTab === 0 && isDepartureSelected) || (activeFlightTab === 1 && isReturnSelected);
              const isInactiveTabDate = (activeFlightTab === 0 && isReturnSelected) || (activeFlightTab === 1 && isDepartureSelected);
              
              const isSelected = isDepartureSelected || isReturnSelected;
              // Only show range when both dates are selected and we want to show the full range
              const isInDateRange = (departDate && returnDateState) ? isInContinuousRange(date) : false;
              const isLowest = isLowestPrice(date);

              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(date)}
                  disabled={isDisabled}
                  className={`
                    flex flex-col items-center justify-center h-16 text-sm transition-colors relative border-2
                    ${isDisabled ? 'text-gray-300 cursor-default border-transparent' : ''}
                    ${!isDisabled && !isSelected && !isInDateRange ? 'cursor-pointer hover:bg-blue-50 text-gray-700 border-transparent' : ''}
                    ${isInDateRange ? 'cursor-pointer hover:bg-blue-100 text-gray-700 border-transparent bg-blue-50' : ''}
                  `}
                  style={{
                    ...(isActiveTabDate && {
                      backgroundColor: '#2563eb',
                      color: 'white',
                      borderColor: '#2563eb',
                      borderTopLeftRadius: isDepartureSelected ? '8px' : '16px'
                    }),
                    ...(isInactiveTabDate && {
                      backgroundColor: '#93c5fd',
                      color: '#1e40af', 
                      borderColor: '#93c5fd',
                      borderTopLeftRadius: isDepartureSelected ? '8px' : '16px'
                    })
                  }}
                >
                  <span className={`text-base font-medium mb-1 ${
                    isDisabled
                      ? 'text-gray-300' 
                      : isActiveTabDate
                        ? 'text-white'
                        : isInactiveTabDate
                          ? 'text-blue-800'
                        : isInDateRange
                          ? 'text-gray-700'
                          : 'text-gray-900'
                  }`}>{date.getDate()}</span>
                  {price && !isDisabled && (
                    <span 
                      className={`text-sm font-medium ${
                        isSelected && isLowest ? 'text-blue-600' : 
                        isActiveTabDate ? 'text-white' : 
                        isInactiveTabDate ? 'text-blue-800' :
                        isInDateRange ? 'text-white' : 
                        isLowest ? 'text-white' : 'text-gray-900'
                      }`}
                      style={isLowest ? {
                        backgroundColor: isSelected ? 'white' : '#008712',
                        borderRadius: '20px',
                        padding: '0 14px',
                        fontSize: '14px',
                        fontFamily: 'AmericanSansMedium, Calibri, Helvetica, Arial, sans-serif',
                        lineHeight: '1.15',
                        WebkitFontSmoothing: 'antialiased'
                      } : {}}
                    >
                      {price}
                    </span>
                  )}
                </button>
              );
            })}
            </div>
            </>
            )}
          </div>

          {/* Right Navigation Arrow */}
          <button
            onClick={nextMonth}
            disabled={isNextMonthDisabled()}
            className={`flex items-center justify-center w-16 h-16 transition-colors z-10 ml-1 ${
              isNextMonthDisabled() 
                ? 'cursor-not-allowed opacity-30' 
                : 'hover:bg-blue-50 cursor-pointer'
            }`}
          >
            <svg className={`w-10 h-10 ${
              isNextMonthDisabled() ? 'text-gray-400' : 'text-aa-blue'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center p-4 border-t border-gray-200 gap-3">
          <button
            onClick={isPreviousMonthDisabled() ? nextMonth : prevMonth}
            className="bg-white text-[#0078d2] border border-[#0078d2] px-20 py-3 rounded text-sm font-medium hover:bg-blue-50"
          >
            {isPreviousMonthDisabled() ? 'Next' : 'Previous'}
          </button>
          <button
            onClick={handleApplyChanges}
            className={`px-16 py-3 rounded text-sm font-medium relative ${
              hasDateChanges() && (tripType === 'roundTrip' ? (departDate && returnDateState) : departDate) 
                ? 'bg-[#0078d2] text-white hover:bg-blue-700 cursor-pointer' 
                : 'bg-[#0078d2] text-white cursor-not-allowed'
            }`}
            disabled={!hasDateChanges() || !(tripType === 'roundTrip' ? (departDate && returnDateState) : departDate)}
          >
            {(!hasDateChanges() || !(tripType === 'roundTrip' ? (departDate && returnDateState) : departDate)) && (
              <div className="absolute inset-0 bg-white opacity-60 rounded"></div>
            )}
            <span className="relative">Apply changes</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarDialog;